<template>
  <!-- No page header — the nav already says Actions, and the row cost is real on
       a list page. `tight` trades the wide gutters for content width. -->
  <PageChrome :show-header="false" tight>
    <div class="actionsPage">
      <div v-if="error" class="pageError">{{ error }}</div>

      <!-- One row: type tabs, stage, then the collapsed secondary filters. -->
      <div class="filterRow">
        <ActionTypeCards
          :modelValue="filters.type"
          :counts="counts"
          :total="total"
          @update:modelValue="(v: string) => setFilters({ type: v })"
        />

        <div class="filterRowRight">
          <div style="width: 140px">
            <SearchSelect
              :modelValue="filters.stage"
              :options="STAGE_OPTIONS"
              placeholder="Status"
              :allow-clear="false"
              hideSearch
              @update:modelValue="(v: string) => setFilters({ stage: v || 'todo' })"
            />
          </div>

          <ActionFilterMenu
            :search="recipientSearch"
            :account="filters.account"
            :play="filters.play"
            :owner="filters.owner"
            :step="filters.step"
            :play-options="playOptions"
            :owner-options="ownerOptions"
            :account-options-fn="searchAccounts"
            :active-count="activeFilterCount"
            @update:search="(v: string) => { recipientSearch = v }"
            @update:account="(v: string) => setFilters({ account: v })"
            @update:play="(v: string) => setFilters({ play: v })"
            @update:owner="(v: string) => setFilters({ owner: v })"
            @update:step="(v: string) => setFilters({ step: v })"
            @clear="clearSecondaryFilters"
          />
        </div>
      </div>

      <ActionsToolbar
        mode="list"
        :page-size="items.length"
        :selected-count="selectedIds.size"
        :page-fully-selected="allOnPageSelected"
        :total="total"
        :select-all-max="SELECT_ALL_MAX"
        :selecting-all="selectAllMatchingLoading"
        :approvable-count="approvableCount"
        :deletable-count="deletableCount"
        :revisable-count="revisableCount"
        :approving="bulkApproving"
        :range-label="rangeLabel"
        :sort="filters.sort"
        :can-prev="filters.page > 1"
        :can-next="filters.page < pageCount"
        @toggle-page="togglePageSelection"
        @select-none="clearSelection"
        @select-all-matching="selectAllMatching"
        @revise="showRevisionPanel = true"
        @approve="bulkApprove"
        @delete="showBulkDeleteDialog = true"
        @prev="goToPage(filters.page - 1)"
        @next="goToPage(filters.page + 1)"
        @update:sort="(v) => setFilters({ sort: v })"
      />

      <div class="listScroll">
        <div v-if="loading && items.length === 0" class="listEmpty">Loading…</div>
        <div v-else-if="items.length === 0" class="listEmpty">No reachouts.</div>

        <div v-else class="rows">
          <div
            v-for="item in items"
            :key="item.id"
            class="row"
            :class="{ selected: String(item.id) === activeId }"
            role="button"
            tabindex="-1"
            @click="openDetail(String(item.id))"
          >
            <input
              type="checkbox"
              :checked="selectedIds.has(String(item.id))"
              @click.stop="toggleSelection(String(item.id), $event)"
            />

            <div class="cellWho">
              <FavIcon
                v-if="item.account_domain"
                class="whoLogo"
                :domain="item.account_domain"
                :size="30"
              />
              <div class="whoText">
                <span class="whoName">{{ contactName(item) }}</span>
                <span v-if="contactCompany(item) || contactTitle(item)" class="whoMeta">
                  <span v-if="contactCompany(item)" class="whoCompany">{{ contactCompany(item) }}</span>
                  <span v-if="contactTitle(item)" class="whoTitle" :title="contactTitle(item)">{{ contactTitle(item) }}</span>
                </span>
              </div>
            </div>

            <!-- What: the type icon inline before the content, so the channel
                 reads as a property of the message rather than its own column. -->
            <div class="cellStack cellPreview">
              <span class="previewTop">
                <AppIcon
                  v-if="item.type === 'reachout_email'"
                  name="email" :size="13" class="previewIcon"
                  style="color:var(--color-scheduled);"
                />
                <FavIcon
                  v-else-if="item.type === 'reachout_linkedin_connect'"
                  domain="linkedin.com" :size="13" class="previewIcon"
                />
                <AppIcon
                  v-else-if="item.type === 'reachout_voicemail'"
                  name="voicemail" :size="13" class="previewIcon"
                  style="color:var(--color-reply);"
                />
                <AppIcon v-else name="reachout" :size="13" class="previewIcon" />
                <span class="previewPrimary">{{ previewPrimary(item) }}</span>
                <span v-if="previewBody(item)" class="previewInlineBody">{{ previewBody(item) }}</span>
              </span>
              <span v-if="item.sequence_name || stepText(item)" class="previewSequence" @click.stop>
                <router-link
                  v-if="item.sequence_name && item.sales_play_id"
                  class="seqPill seqPillLink"
                  :to="`/sales-plays/${item.sales_play_id}/sequence`"
                  :title="`Open sequence: ${item.sequence_name}`"
                >
                  <AppIcon name="sequence" :size="11" class="seqPillIcon" />
                  <span class="seqPillText">{{ item.sequence_name }}</span>
                </router-link>
                <span v-else-if="item.sequence_name" class="seqPill">
                  <AppIcon name="sequence" :size="11" class="seqPillIcon" />
                  <span class="seqPillText">{{ item.sequence_name }}</span>
                </span>
                <span v-if="stepText(item)" class="seqStep">{{ stepText(item) }}</span>
              </span>
            </div>

            <div class="cellStack cellOwner">
              <span :class="['stackPrimary', { unassigned: !item.owner_name }]">{{ item.owner_name || 'Unassigned' }}</span>
              <span class="statusLine">
                <span class="statusPill" :data-status="item.lifecycle_state || item.status">
                  {{ statusPillText(item) }}
                </span>
                <span class="indicators" @click.stop>
                  <template v-if="crmSync(item)">
                    <a
                      v-if="crmSync(item)!.href"
                      class="indicatorLink"
                      :href="crmSync(item)!.href!"
                      target="_blank"
                      rel="noopener"
                      :title="`Open ${crmSync(item)!.label} activity`"
                    >
                      <FavIcon :domain="crmSync(item)!.domain" :size="14" />
                    </a>
                    <span v-else class="indicatorLink" :title="`Synced to ${crmSync(item)!.label}`">
                      <FavIcon :domain="crmSync(item)!.domain" :size="14" />
                    </span>
                  </template>

                  <a
                    v-if="item.slack_permalink"
                    class="indicatorLink"
                    :href="item.slack_permalink"
                    target="_blank"
                    rel="noopener"
                    title="Open Slack notification"
                  >
                    <img class="integrationIcon" :src="slackIconSrc" alt="Slack" loading="lazy" draggable="false" />
                  </a>
                  <span v-else-if="item.payload?.slack_notification?.ok" class="indicatorLink" title="Slack notification sent (no link available)">
                    <img class="integrationIcon" :src="slackIconSrc" alt="Slack" loading="lazy" draggable="false" />
                  </span>
                </span>
              </span>
            </div>

            <div class="cellStack cellDate">
              <span class="stackPrimary">{{ dateOnly(item.created_at) }}</span>
              <span class="stackSecondary">{{ timeOnly(item.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Bulk delete dialog -->
    <div v-if="showBulkDeleteDialog" class="dismissOverlay" @click.self="showBulkDeleteDialog = false">
      <div class="dismissDialog" role="dialog" aria-modal="true">
        <div class="dismissHeader">
          <span class="dismissTitle">Delete {{ deletableCount }} reachout{{ deletableCount > 1 ? 's' : '' }}…</span>
          <button type="button" class="dismissClose" aria-label="Close" @click="showBulkDeleteDialog = false">✕</button>
        </div>
        <div class="dismissBody">
          <p class="dismissHint">After deletion, each sequence should…</p>
          <label v-for="opt in DISMISS_OPTIONS" :key="opt.value" class="dismissOption">
            <input type="radio" name="bulkDismissReason" :value="opt.value" v-model="bulkDismissReason" />
            <span>{{ opt.label }}</span>
          </label>
          <textarea
            v-model="bulkDismissNotes"
            class="input dismissNotes"
            placeholder="Notes (optional)"
            rows="3"
          />
        </div>
        <div class="dismissFooter">
          <button type="button" class="btn" :disabled="bulkDeleting" @click="showBulkDeleteDialog = false">Cancel</button>
          <button type="button" class="btn danger" :disabled="bulkDeleting || !bulkDismissReason" @click="confirmBulkDelete">
            {{ bulkDeleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <RevisionPanel
      v-if="showRevisionPanel"
      :reachoutIds="revisableIds(selectedIds)"
      @close="showRevisionPanel = false"
      @revised="handleRevisionComplete"
    />

    <template #footer>
      <ShortcutBar :hotkeys="hotkeys" />
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import FavIcon from '../../components/FavIcon.vue'
import RevisionPanel from '../../components/RevisionPanel.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import ActionTypeCards from '../components/actions/ActionTypeCards.vue'
import ActionFilterMenu from '../components/actions/ActionFilterMenu.vue'
import ActionsToolbar from '../components/actions/ActionsToolbar.vue'
import { apiFetch } from '../../services/api'
import { googleFaviconUrl } from '../../services/favicons'
import { hubspotCallReviewUrl, preloadCrmLinkConfig, salesforceTaskReviewUrl } from '../../services/crmLinks'
import { useChatScope } from '../composables/useChatScope'
import { useHotkeys, type Hotkey } from '../composables/useHotkeys'
import ShortcutBar from '../components/ShortcutBar.vue'
import { SELECT_ALL_MAX, STAGE_OPTIONS, useActionsQuery } from '../composables/useActionsQuery'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_REACHOUT } from '../../drawer/drawerTypes'

/**
 * The Actions list — full-width, Gmail-style: rows here, the reachout itself on
 * its own route (see ActionDetailPage). The old master-detail split left the
 * reachout too narrow to actually review, and cost the row the horizontal room
 * for the sequence-step and subject columns.
 *
 * All filter/page state lives in the URL via useActionsQuery, so back/forward
 * move through filter and page history. Row selection (the j/k cursor) is
 * deliberately NOT in the URL — it isn't worth a history entry, and keeping it
 * out is what frees the query string for the filters.
 */

const slackIconSrc = googleFaviconUrl('slack.com', { size: 32 })

type CrmSyncIndicator = { label: string; domain: string; href: string | null }

function crmSync(item: any): CrmSyncIndicator | null {
  if (item?.hubspot_engagement_id) {
    return {
      label: 'HubSpot',
      domain: 'hubspot.com',
      href: hubspotCallReviewUrl(item.hubspot_engagement_id),
    }
  }
  if (item?.salesforce_task_id) {
    return {
      label: 'Salesforce',
      domain: 'salesforce.com',
      href: salesforceTaskReviewUrl(item.salesforce_task_id),
    }
  }
  return null
}

const { setScope, clearScope } = useChatScope()
const { setContext: setDrawerContext, pinDrawer } = useContextDrawer()
pinDrawer()

const query = useActionsQuery()
const {
  filters, items, total, counts, loading,
  pageCount, rangeStart, rangeEnd,
  setFilters, goToPage, openDetail, refresh, load,
} = query

setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: { page_type: 'actions' } })

/**
 * Bulk-action errors live here rather than on the query, whose `error` is
 * cleared by every load — the 3s poll would wipe a bulk failure before it was
 * read.
 */
const actionError = ref('')
const error = computed(() => actionError.value || query.error.value)

const rangeLabel = computed(() =>
  total.value > 0 ? `${rangeStart.value}–${rangeEnd.value} of ${total.value}` : '0 of 0',
)

const selectedIds = ref<Set<string>>(new Set())
/**
 * The selected rows themselves, kept because selection spans pages while
 * `items` only ever holds the current one. Bulk actions need `user_can_act` for
 * rows the user has paged away from.
 */
const selectedRows = ref<Map<string, any>>(new Map())
const lastClickedId = ref<string | null>(null)
const showRevisionPanel = ref(false)

/**
 * The j/k cursor. Not URL state (see the component doc), but shared via the
 * query composable rather than local — so it survives navigating into a detail
 * view and back, and reflects wherever you ended up if you j/k'd through several.
 */
const activeId = query.cursorId

const playOptions = ref<{ value: string; label: string }[]>([])
const ownerOptions = ref<{ value: string; label: string }[]>([])

/**
 * Badge on the filter icon. Counts only the filters hidden inside the menu —
 * type and stage stay visible on the row, so including them would report a
 * filter the user can already see.
 */
const activeFilterCount = computed(() =>
  [filters.value.account, filters.value.play, filters.value.owner, filters.value.step, filters.value.q]
    .filter(Boolean).length,
)

function clearSecondaryFilters() {
  recipientSearch.value = ''
  setFilters({ account: '', play: '', owner: '', step: '', q: '' })
}


// ── Recipient search: debounced into the URL ──────────────────────
// Typing pushes a route per keystroke otherwise, which would bury the
// back button in history entries.

const recipientSearch = ref(filters.value.q)
let searchTimer: ReturnType<typeof setTimeout> | null = null

watch(recipientSearch, (value) => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    if (value.trim() !== filters.value.q) setFilters({ q: value.trim() })
  }, 300)
})

// Keep the box in step when the URL changes underneath it (back button).
watch(() => filters.value.q, (q) => {
  if (q !== recipientSearch.value.trim()) recipientSearch.value = q
})

// ── Filter options ───────────────────────────────────────────────

async function loadFilterOptions() {
  try {
    const data = await apiFetch('/reachouts/filter-options')
    playOptions.value = Array.isArray(data?.sales_plays) ? data.sales_plays : []
    ownerOptions.value = Array.isArray(data?.owners) ? data.owners : []
  } catch {
    // Dropdowns degrade to empty; the URL filters still work.
  }
}

async function searchAccounts(q: string) {
  const qs = new URLSearchParams({ q, limit: '20' })
  const resp = await apiFetch(`/accounts/paginated?${qs}`)
  const list = Array.isArray(resp?.items) ? resp.items : []
  return list.map((a: any) => ({
    value: String(a.id || ''),
    label: String(a.name || a.domain || '').trim() || String(a.domain || ''),
    domain: String(a.domain || ''),
  }))
}

// ── Row display ──────────────────────────────────────────────────

function contactName(item: any): string {
  const ctx = item?.recipient_context
  return String(ctx?.name || '').trim() || String(item?.to || '').trim() || '—'
}

function contactCompany(item: any): string {
  return String(item?.account_name || item?.account_domain || '').trim()
}

function contactTitle(item: any): string {
  return String(item?.recipient_context?.title || '').trim()
}

/**
 * Short fixed labels standing in for the subject on types that don't have a real
 * one. Their stored subject is a constant ("Voicemail", "LinkedIn connection
 * request") that's both redundant with the type icon and too long for the slot.
 */
const PREVIEW_LABELS: Record<string, string> = {
  reachout_voicemail: 'Voicemail',
  reachout_linkedin_connect: 'LI Connect',
}

/**
 * Leading label: the real subject for email, a short constant otherwise. Every
 * type now gets label + body in the same two slots, so the layout has one shape
 * to get right instead of a per-type special case.
 */
function previewPrimary(item: any): string {
  const label = PREVIEW_LABELS[item?.type]
  if (label) return label
  return String(item?.subject || '').trim() || '(no subject)'
}

/** Body text, inline after the label — for every type now, not just email. */
function previewBody(item: any): string {
  return bodyPreview(item)
}

function stepText(item: any): string {
  const index = item?.sequence_step_index
  if (typeof index !== 'number') return ''
  const total = item?.sequence_step_count
  return typeof total === 'number' ? `Step ${index + 1} of ${total}` : `Step ${index + 1}`
}

function bodyPreview(item: any): string {
  return String(item?.body || '').replace(/\s+/g, ' ').trim()
}


/** Split date and time so they can stack in one column. */
function dateOnly(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString([], { month: 'short', day: '2-digit' })
}

function timeOnly(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// ── Selection (checkboxes, for bulk actions) ─────────────────────

function select(row: any) {
  const id = String(row?.id || '')
  if (!id) return
  selectedIds.value.add(id)
  selectedRows.value.set(id, row)
}

function deselect(id: string) {
  selectedIds.value.delete(id)
  selectedRows.value.delete(id)
}

function toggleSelection(id: string, event: MouseEvent) {
  if (event.shiftKey && lastClickedId.value !== null) {
    const ids = items.value.map((r: any) => String(r.id))
    const from = ids.indexOf(lastClickedId.value)
    const to = ids.indexOf(id)
    if (from !== -1 && to !== -1) {
      const [lo, hi] = from < to ? [from, to] : [to, from]
      for (let i = lo; i <= hi; i++) select(items.value[i])
      lastClickedId.value = id
      return
    }
  }
  if (selectedIds.value.has(id)) deselect(id)
  else select(items.value.find((r: any) => String(r.id) === id))
  lastClickedId.value = id
}

/**
 * The master checkbox: selects the page, or clears everything if the page is
 * already fully selected. Clearing drops off-page selections too — with the
 * escalation banner able to select beyond this page, leaving those behind
 * would make the checkbox look like it did nothing.
 */
function togglePageSelection() {
  if (allOnPageSelected.value) clearSelection()
  else items.value.forEach((r: any) => select(r))
}

/** Every row matching the current filters, not just the loaded page. */
const selectAllMatchingLoading = ref(false)

async function selectAllMatching() {
  if (selectAllMatchingLoading.value) return
  selectAllMatchingLoading.value = true
  try {
    const rows = await query.fetchAllMatching()
    rows.forEach((r: any) => select(r))
  } catch (e: any) {
    actionError.value = e?.message || 'Could not select all matching actions.'
  } finally {
    selectAllMatchingLoading.value = false
  }
}

function clearSelection() {
  selectedIds.value.clear()
  selectedRows.value.clear()
  lastClickedId.value = null
}

/**
 * This mirrors the server's _user_can_act_on_reachout, which is
 * focused on reachout ownership.
 *
 * Resolved against selectedRows rather than the current page: selection spans
 * pages, and an off-page id isn't in `items`, so looking there would skip the
 * permission check for exactly the rows the user can no longer see.
 */
function ownedIds(ids: Set<string>): string[] {
  return [...ids].filter((id) => selectedRows.value.get(id)?.user_can_act !== false)
}

function statusOf(id: string): string | undefined {
  return selectedRows.value.get(id)?.status
}

function statusPillText(item: { lifecycle_state?: string | null; status: string }): string {
  if (item.status === 'needs_mailbox') return 'Needs a mailbox'
  return item.lifecycle_state || item.status
}

/** Mirrors _approve_reachout_impl: email/voicemail approve only from pending_approval. */
function approvableIds(ids: Set<string>): string[] {
  return ownedIds(ids).filter((id) => statusOf(id) === 'pending_approval')
}

const approvableCount = computed(() => approvableIds(selectedIds.value).length)

/** Mirrors _dismiss_reachout_impl. */
const UNDISMISSABLE_STATUSES = new Set(['sent', 'sending', 'completed', 'deleted'])

function deletableIds(ids: Set<string>): string[] {
  return ownedIds(ids).filter((id) => !UNDISMISSABLE_STATUSES.has(statusOf(id) ?? ''))
}

const deletableCount = computed(() => deletableIds(selectedIds.value).length)

function revisableIds(ids: Set<string>): string[] {
  return ownedIds(ids).filter((id) => statusOf(id) === 'pending_approval' || statusOf(id) === 'needs_mailbox')
}

const revisableCount = computed(() => revisableIds(selectedIds.value).length)

const allOnPageSelected = computed(() =>
  items.value.length > 0 && items.value.every((r: any) => selectedIds.value.has(String(r.id))),
)

// ── Bulk actions ─────────────────────────────────────────────────

const bulkApproving = ref(false)

async function bulkApprove() {
  if (bulkApproving.value) return
  bulkApproving.value = true
  actionError.value = ''

  const ids = approvableIds(selectedIds.value)
  if (!ids.length) { bulkApproving.value = false; clearSelection(); return }
  try {
    await apiFetch(`/reachouts/bulk-approve`, { method: 'POST', body: { ids } })
    // Per-row skips (already sent, wrong status, etc.) are intentionally
    // not surfaced — the list reload reflects what actually changed.
  } catch (e: any) {
    actionError.value = e?.message || 'Bulk approve failed'
  }
  bulkApproving.value = false
  clearSelection()
  await refresh()
}

const showBulkDeleteDialog = ref(false)
const bulkDismissReason = ref('')
const bulkDismissNotes = ref('')
const bulkDeleting = ref(false)

const DISMISS_OPTIONS = [
  { value: 'skip',            label: 'Skip to next step' },
  { value: 'engaged',         label: 'Exit sequence — engaged' },
  { value: 'disqualified',    label: 'Exit sequence — disqualified' },
  { value: 'bad_icp_fit',     label: 'Exit sequence — bad ICP fit for account' },
  { value: 'bad_persona_fit', label: 'Exit sequence — bad persona fit for prospect' },
]

async function confirmBulkDelete() {
  bulkDeleting.value = true
  actionError.value = ''
  const ids = deletableIds(selectedIds.value)
  if (!ids.length) { bulkDeleting.value = false; showBulkDeleteDialog.value = false; clearSelection(); return }
  try {
    await apiFetch(`/reachouts/bulk-dismiss`, {
      method: 'POST',
      body: {
        ids,
        exit_reason: bulkDismissReason.value,
        exit_notes: bulkDismissNotes.value || null,
      },
    })
  } catch (e: any) {
    actionError.value = e?.message || 'Bulk delete failed'
  }
  bulkDeleting.value = false
  showBulkDeleteDialog.value = false
  bulkDismissReason.value = ''
  bulkDismissNotes.value = ''
  clearSelection()
  await refresh()
}

function handleRevisionComplete() {
  clearSelection()
  showRevisionPanel.value = false
  void refresh()
}

// ── Keyboard: j/k move the cursor, Enter opens ────────────────────

function moveCursor(delta: number) {
  const ids = items.value.map((r: any) => String(r.id))
  if (!ids.length) return
  const current = ids.indexOf(activeId.value)
  if (current === -1) {
    activeId.value = ids[delta > 0 ? 0 : ids.length - 1]
    scrollCursorIntoView()
    return
  }
  const next = current + delta
  if (next >= 0 && next < ids.length) {
    activeId.value = ids[next]
    scrollCursorIntoView()
    return
  }
  // Off the end of the page: page across, so paging and the keyboard behave as
  // one list rather than two systems.
  if (next < 0 && filters.value.page > 1) {
    cursorAfterLoad.value = 'last'
    goToPage(filters.value.page - 1)
  } else if (next >= ids.length && filters.value.page < pageCount.value) {
    cursorAfterLoad.value = 'first'
    goToPage(filters.value.page + 1)
  }
}

/** Which end to park the cursor on once the next page's rows arrive. */
const cursorAfterLoad = ref<'first' | 'last' | null>(null)

/**
 * Move DOM focus to the cursor row, not just the highlight.
 *
 * Without this the cursor is purely visual: focus stays wherever the user last
 * clicked — a filter dropdown, the search box — so Enter goes to that control
 * instead of opening the row. Focusing the row also makes the native Enter
 * behaviour of `role="button"` do the right thing.
 */
function scrollCursorIntoView() {
  requestAnimationFrame(() => {
    const el = document.querySelector('.row.selected') as HTMLElement | null
    if (!el) return
    el.scrollIntoView({ block: 'nearest' })
    // Don't steal focus from someone typing in the filter bar.
    if (!isTextEntryFocused()) el.focus({ preventScroll: true })
  })
}

function isTextEntryFocused(): boolean {
  const el = document.activeElement
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true
  if (el.tagName !== 'INPUT') return false
  const type = (el as HTMLInputElement).type?.toLowerCase() || 'text'
  return type !== 'checkbox' && type !== 'radio' && type !== 'button'
}

watch(items, (rows) => {
  const ids = rows.map((r: any) => String(r.id))
  if (cursorAfterLoad.value) {
    activeId.value = cursorAfterLoad.value === 'first' ? (ids[0] || '') : (ids[ids.length - 1] || '')
    cursorAfterLoad.value = null
    scrollCursorIntoView()
    return
  }
  // Keep the cursor on a real row after a filter change or a delete.
  if (activeId.value && !ids.includes(activeId.value)) activeId.value = ids[0] || ''
  else if (!activeId.value) activeId.value = ids[0] || ''
})

/**
 * Context drawer (the `\` panel), following the j/k cursor.
 *
 * The old master-detail page sourced this from the selected row because
 * selection *was* the detail view. Here the cursor is the nearest equivalent —
 * it's what "the row you're looking at" means on a list — so the drawer tracks
 * it and `\` works without opening the reachout first.
 */
const cursorRow = computed(() => items.value.find((r: any) => String(r.id) === activeId.value) || null)

watch(cursorRow, (row) => {
  const accountId = String(row?.account_id || '').trim()
  const id = String(row?.id || '').trim()
  if (!id || !accountId) {
    setDrawerContext(null)
    return
  }
  setDrawerContext({
    type: DRAWER_CONTEXT_REACHOUT,
    reachoutId: id,
    accountId,
    contactId: String(row?.contact_id || '').trim(),
    agentRunId: String(row?.created_by_agent_run_id || '').trim(),
  })
}, { immediate: true })

// Arrows mirror j/k, carrying over the master-detail behaviour this list
// replaced. They preventDefault so the row cursor moves instead of the
// scroll container jumping a viewport.
function arrowHandler(delta: number) {
  return (e: KeyboardEvent) => {
    e.preventDefault()
    moveCursor(delta)
  }
}


const hotkeys: Hotkey[] = [
  { key: 'j', handler: () => moveCursor(1), description: 'next', primary: true, group: 'Navigate' },
  { key: 'k', handler: () => moveCursor(-1), description: 'previous', primary: true, group: 'Navigate' },
  { key: 'arrowdown', handler: arrowHandler(1), description: 'next', group: 'Navigate' },
  { key: 'arrowup', handler: arrowHandler(-1), description: 'previous', group: 'Navigate' },
  {
    key: 'x',
    handler: () => {
      const id = activeId.value
      if (!id) return
      if (selectedIds.value.has(id)) deselect(id)
      else select(items.value.find((r: any) => String(r.id) === id))
      lastClickedId.value = id
    },
    description: 'select',
    primary: true,
    group: 'Select',
  },
  {
    key: 'mod+shift+a',
    handler: (e) => { e.preventDefault(); togglePageSelection() },
    description: 'select page',
    group: 'Select',
  },  
  {
    key: 'enter',
    // preventDefault matters here: useHotkeys runs handlers without suppressing
    // the native default, so a focused row (role="button") or button would also
    // fire its own activation and reload the page on top of our router push.
    handler: (e) => {
      if (!activeId.value) return
      e.preventDefault()
      openDetail(activeId.value)
    },
    description: 'open',
    primary: true,
    group: 'Navigate',
  },
  {
    key: 'o',
    handler: (e) => {
      if (!activeId.value) return
      e.preventDefault()
      openDetail(activeId.value)
    },
    description: 'open',
    group: 'Navigate',
  },
  {
    key: 'delete',
    handler: (e) => {
      if (!deletableCount.value) return
      e.preventDefault()
      showBulkDeleteDialog.value = true
    },
    description: 'delete',
    primary: true,
    group: 'Selected actions',
  },
  {
    key: 'mod+r',
    handler: (e) => {
      if (!revisableCount.value) return
      e.preventDefault()
      showRevisionPanel.value = true
    },
    description: 'revise',
    primary: true,
    group: 'Selected actions',
  },  
  {
    key: 'mod+enter',
    handler: (e) => {
      if (!approvableCount.value || bulkApproving.value) return
      e.preventDefault()
      void bulkApprove()
    },
    description: 'approve & send',
    primary: true,
    group: 'Selected actions',
  },  
]

useHotkeys(hotkeys)

// ── Poll ─────────────────────────────────────────────────────────
// Only the current page now, rather than every infinite-scrolled row.

let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  void loadFilterOptions()
  // Warm the CRM deep-link config so the row icons resolve to links. Idempotent
  // and shared with the dialer pages.
  void preloadCrmLinkConfig()
  if (activeId.value) scrollCursorIntoView()
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') void load({ forPoll: true })
  }, 3000)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
  if (searchTimer) clearTimeout(searchTimer)
  clearScope()
  setDrawerContext(null)
})
</script>

<style scoped>
/* ── Local aliases so the page and its children inherit the next palette ── */
.actionsPage {
  --border: var(--color-border-subtle);
  --border2: var(--color-border-subtle);
  --surface: var(--color-bg-surface);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --badge-bg: var(--color-bg-subtle);
  --row-hover-bg: var(--color-bg-subtle);
  --hover-bg: var(--color-bg-subtle);
  --link: var(--color-accent);
  --panel: var(--color-bg-surface);
  --field-bg: var(--color-bg-subtle);
  --table-header-bg: var(--color-bg-subtle);
  --focus-border: var(--color-accent);

  font-family: var(--font-sans);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.pageError {
  color: var(--color-danger);
  padding: var(--space-3) var(--space-5);
  font-size: var(--fs-sm);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

/* Type tabs left, stage + collapsed filters right — one row instead of the old
   filter bar plus a card grid. */
.filterRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0 0 var(--space-2);
  flex-shrink: 0;
  min-width: 0;
}
.filterRowRight {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
  flex-shrink: 0;
}

/* ── List ─────────────────────────────────────────────────────── */
/* Square top and no top border: the toolbar directly above supplies the divider,
   so the bar and the table read as one panel. */
.listScroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border: 1px solid var(--color-border-subtle);
  border-top: none;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  background: var(--color-bg-app);
}
.listEmpty { padding: var(--space-6); color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.rows { display: flex; flex-direction: column; }

.row {
  display: grid;
  /* Every text column is minmax(0, …fr) so it can actually shrink — `auto`
     columns refuse to compress and overlap once the context drawer takes a
     third of the width. Owner/status and date/time each stack two lines, which
     is what buys the horizontal room back. */
  grid-template-columns:
    20px                  /* checkbox */
    minmax(0, 1.2fr)      /* contact name / title / account */
    minmax(0, 2.4fr)      /* preview — type icon is inline here, not its own column */
    /* Fixed width, not max-content: content-sized columns resolve per row, so
       the boundary wanders down the list and the table stops reading as a
       table. Narrower than before, but the same on every row. */
    170px                 /* owner / status + integration icons */
    max-content;          /* date / time — never wraps */
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border-subtle);
  cursor: pointer;
  text-align: left;
  min-width: 0;
}

/* ── Two-line cells ───────────────────────────────────────────── */
/* ── Two-line row columns ──────────────────────────────────────────
   Every two-line column is built the same way so the baselines line up across
   the table: 13px top line, 11px bottom, both on a FIXED line box
   (--row-line-top / --row-line-bottom) and the same gap. */
.row {
  --row-line-top: 20px;
  --row-line-bottom: 18px;
  --row-line-gap: 2px;
}
.cellStack,
.whoText {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--row-line-gap);
  min-width: 0;
}
/* Top line of any column. */
.whoName,
.previewTop,
.cellStack > .stackPrimary {
  min-height: var(--row-line-top);
  line-height: var(--row-line-top);
}
/* Bottom line of any column. */
.whoMeta,
.previewSequence,
.cellStack > .statusLine,
.cellStack > .stackSecondary {
  min-height: var(--row-line-bottom);
  line-height: var(--row-line-bottom);
}
.stackPrimary,
.stackSecondary {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stackPrimary {
  font-size: 14px;
  color: var(--color-text-secondary);
}
.stackPrimary.unassigned { color: var(--color-text-tertiary); font-style: italic; }
.stackSecondary {
  font-size: 12px;
  color: var(--color-text-tertiary);
}
.row:last-child { border-bottom: none; }
.row.selected { background: var(--color-bg-surface); box-shadow: inset 2px 0 0 var(--color-accent); }
.row:focus,
.row:focus-visible { outline: none; }

/* ── Who: account logo + name over "Company · Title" ──────────── */
.cellWho {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-left: var(--space-2);
  min-width: 0;
}
/* Spans both text lines, so it reads as belonging to the pair rather than to
   either line. Never shrinks — the text truncates instead. */
.whoLogo {
  flex-shrink: 0;
  border-radius: var(--radius-md);
}
.whoName,
.whoCompany,
.whoTitle {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.whoName {
  font-size: 14px;
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.whoMeta {
  display: flex;
  max-width: 100%;
  font-size: 12px;
}
/* Company is what people scan for, so only the title gives way to the ellipsis. */
.whoCompany {
  flex-shrink: 0;
  font-weight: var(--fw-medium);
  color: var(--color-text-secondary);
}
.whoTitle {
  min-width: 0;
  color: var(--color-text-tertiary);
}
.whoCompany + .whoTitle::before {
  content: '·';
  margin: 0 var(--space-1);
}

/* ── Preview: type icon + subject / body ──────────────────────── */
.previewTop {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  max-width: 100%;
  min-width: 0;
}
.previewIcon { flex-shrink: 0; }
.previewPrimary {
  flex: 0 1 auto;
  min-width: max-content;
  max-width: 60%;
  font-size: 14px;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.previewInlineBody {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Sequence + step, second line of the preview ──────────────── */
.previewSequence {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  max-width: 100%;
  min-width: 0;
}
.seqPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  height: var(--row-line-bottom);
  padding: 0 var(--space-2);
  border-radius: var(--radius-full);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  min-width: 0;
}
.seqPillIcon { flex-shrink: 0; }
.seqPillText {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.seqPillLink { text-decoration: none; }
.seqPillLink:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
/* The step is a plain marker, not a link — no pill chrome competing with one. */
.seqStep {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
}

.cellDate { font-variant-numeric: tabular-nums; }
.statusLine { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }



/* ── Integration indicators ───────────────────────────────────── */
.indicators { display: inline-flex; align-items: center; gap: 6px; }
.indicatorLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.indicatorLink:hover { background: var(--color-bg-subtle); }
.integrationIcon {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  object-fit: contain;
  display: block;
}

/* ── Status pill — neumorphic micro badge ─────────────────────── */
.statusPill {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  height: var(--row-line-bottom);
  padding: 0 var(--space-2);
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  white-space: nowrap;
  /* In the owner/status stack: don't stretch to the column, and truncate rather
     than force the column wider (statuses like "Connected — not interested"
     are long). */
  align-self: flex-start;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.statusPill[data-status='pending'],
.statusPill[data-status='pending_approval'] { background: var(--color-bg-surface); color: var(--color-text-secondary); }
.statusPill[data-status='revision_in_progress'] { background: var(--color-bg-surface); color: var(--hue-purple); }
.statusPill[data-status='queued_for_sending'] { background: var(--color-scheduled-soft); color: var(--color-scheduled); }
.statusPill[data-status='sending'] { background: var(--hue-amber-soft); color: var(--hue-amber); }
.statusPill[data-status='sent'] { background: var(--color-bg-surface); color: var(--color-text-secondary); }
.statusPill[data-status='delivered'] { background: var(--color-success-soft); color: var(--color-success); }
.statusPill[data-status='opened'] { background: var(--color-scheduled-soft); color: var(--color-scheduled); }
.statusPill[data-status='replied'] { background: var(--color-reply-soft); color: var(--color-reply); }
.statusPill[data-status='bounced'] { background: var(--hue-amber-soft); color: var(--hue-amber); }
.statusPill[data-status='spam'],
.statusPill[data-status='send_failed'] { background: var(--color-danger-soft); color: var(--color-danger); }
.statusPill[data-status='voicemail_left'] { background: var(--color-success-soft); color: var(--color-success); }
.statusPill[data-status='call_answered'] { background: var(--hue-amber-soft); color: var(--hue-amber); }
.statusPill[data-status='no_answer'] { background: var(--color-bg-subtle); color: var(--color-text-tertiary); }
.statusPill[data-status='deleted'] { background: var(--color-bg-subtle); color: var(--color-text-tertiary); }

/* ── Bulk delete dialog ──────────────────────────────────────── */
.dismissOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dismissDialog {
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
  width: 380px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.dismissHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--color-border-subtle);
}

.dismissTitle {
  font-weight: 700;
  font-size: 14px;
  color: var(--color-text-primary);
}

.dismissClose {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: 14px;
  padding: 2px 4px;
  line-height: 1;
}

.dismissBody {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dismissHint {
  margin: 0 0 6px;
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
}

.dismissOption {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  padding: 4px 0;
}

.dismissOption input[type="radio"] {
  cursor: pointer;
  flex: none;
}

.dismissNotes {
  margin-top: 8px;
  width: 100%;
  resize: vertical;
  font-size: var(--fs-sm);
}

.dismissFooter {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--color-border-subtle);
}
</style>
